import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LanguageMaster from './languageMaster';
import MasterDataTranslation from './MasterDataTranslation';

const Translation = () => {
    const { t } = useTranslation('translationpage');
    const [activeTab, setActiveTab] = useState('language'); // Initially set to 'language'
    const [mobileTabOpen, setMobileTabOpen] = useState(false);

    const tabs = [
        { id: 'language', label: t('translationpage.language_tab', 'Language') },
        { id: 'masterData', label: t('translationpage.master_data_tab', 'Master Data') }
    ];

    const handleTabClick = (e, key) => {
        setActiveTab(key);
    };

    // Close mobile dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mobileTabOpen && !event.target.closest('[data-testid~="translation-mobile-tabs-dropdown"]')) {
                setMobileTabOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [mobileTabOpen]);

    return (
        <div className="w-full" data-testid="master-translation-container">
            {/* Modern Navigation Tabs */}
            <div className="mb-4" data-testid="master-translation-tabs-wrapper">
                {/* Desktop Navigation Tabs */}
                <div className="hidden w-fit sm:flex flex-wrap justify-start gap-2 bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 relative" data-testid="master-translation-desktop-tabs">
                    {tabs.map(({ id, label }) => (
                        <button
                            key={id}
                            onClick={(e) => handleTabClick(e, id)}
                            className={`group relative px-6 py-3 text-sm font-semibold transition-all duration-300 rounded-xl z-10
                                ${activeTab === id
                                    ? 'text-white'
                                    : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                                }`}
                            data-testid={`master-translation-tab-${id}`}
                        >
                            {activeTab === id && (
                                <motion.div
                                    layoutId="activeTranslationTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20" data-testid={`master-translation-tab-label-${id}`}>{label}</span>
                        </button>
                    ))}
                </div>

                {/* Mobile Navigation - Dropdown Style */}
                <div data-testid="translation-mobile-tabs-dropdown" className="sm:hidden">
                    <div className="bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-2xl shadow-lg border border-gray-200 p-2" data-testid="master-translation-mobile-tabs-card">
                        <div className="relative">
                            <button
                                className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 text-gray-700 font-semibold cursor-pointer"
                                onClick={() => setMobileTabOpen(!mobileTabOpen)}
                                data-testid="master-translation-mobile-tab-trigger"
                            >
                                <span data-testid="master-translation-mobile-tab-active-label">{tabs.find(tab => tab.id === activeTab)?.label}</span>
                                <svg
                                    className={`w-5 h-5 transition-transform duration-300 ${mobileTabOpen ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    data-testid="master-translation-mobile-tab-chevron"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {mobileTabOpen && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden" data-testid="master-translation-mobile-tab-menu">
                                    {tabs.map(({ id, label }) => (
                                        <button
                                            key={id}
                                            onClick={(e) => {
                                                handleTabClick(e, id);
                                                setMobileTabOpen(false);
                                            }}
                                            className={`w-full text-left px-4 py-3 text-sm font-semibold transition-all duration-300 border-b border-gray-100 last:border-b-0 cursor-pointer
                                                ${activeTab === id
                                                    ? 'text-white bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A]'
                                                    : 'text-gray-600 hover:text-[#4ABDBD] hover:bg-gray-50'
                                                }`}
                                            data-testid={`master-translation-mobile-tab-option-${id}`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tab Content */}
            <div className=" pb-0" data-testid="master-translation-tab-content">
                {activeTab === 'language' && <LanguageMaster />}
                {activeTab === 'masterData' && <MasterDataTranslation />}
            </div>
        </div>
    );
};

export default Translation;

